<template>
  <div>
    <Grid
      @api="getCompanies()"
      :headers="headers"
      :isCached="true"
      :payload="{results, count, next, previous}"
      @filter="showFilterModal = true"
      @clearFilters="$refs.desktopFilters.reset()"
    >
      <template #buttons>
        <base-button @click="$router.push('companies/create')" size="sm" action="create_company">
          <base-icon icon="file-plus" class="text-lg" />
        </base-button>
        <base-button @click="$router.push('crm-import')" size="sm" action="import_company">
          <base-icon icon="import" class="text-lg" />
        </base-button>
      </template>

      <template #filter>
        <CompaniesFilters ref="desktopFilters" />
      </template>

      <template #head.actions>
        <div class="bg-secondary"></div>
      </template>

      <template #body.name="{item}">
        <div class="flex flex-col">
          <div :class="['flex flex-col lg:flex-row lg:items-center leading-5 font-medium']">
            <span>{{ item.name }}</span>
            <div v-if="item.is_distributor" class="flex-shrink-0 lg:ml-2">
              <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-white bg-blue-light block"
                >{{ $t(`mygwf.components.Grid.distributor`) }}</span
              >
            </div>
          </div>
          <div class="hidden pt-1 lg:block leading-5 text-secondary-dark">
            {{ Array.from([item.address, item.city, item.country]).filter(Boolean).join(', ') }}
          </div>
        </div>
      </template>
      <template #body.is_active="{item}">
        <span
          :class="[
            'px-2 inline-flex leading-6 font-semibold rounded text-white',
            item.is_active ? 'bg-green-light' : 'bg-orange-light',
          ]"
        >
          {{
            item.is_active
              ? $t('mygwf.components.Grid.active')
              : $t('mygwf.components.Grid.inactive')
          }}
        </span>
      </template>
      <template #body.customer_account="{item}">
        <span
          class="px-2 inline-flex leading-6 font-semibold rounded text-gray-600 bg-secondary block"
          >{{ item.area }}-{{ item.customer_account }}</span
        >
      </template>
      <template #body.distributor="{item}">
        <span>{{ companiesMap[item.distributor] }}</span>
      </template>
      <template #body.actions="{item}">
        <div class="flex justify-end space-x-4">
          <base-button-icon
            v-if="!item.is_active"
            @click="activateCompany(item)"
            action="activate_company"
            class="text-orange-default"
            :loading="loading && item.id === clickedCompanyId"
          />
          <base-button-icon
            @click="toggleCompanyDetails(item.id)"
            :tooltip="item.name"
            :icon="visibleCompanies.includes(item.id) ? `expand-up` : `expand-down`"
            class="bg-secondary-light"
          />
        </div>
      </template>

      <template #expanded="{item}">
        <transition name="slidedown" class="slidedown transition-all">
          <div v-if="visibleCompanies.includes(item.id)">
            <div class="grid-expanded">
              <div class="mx-auto w-full max-w-4xl">
                <base-card
                  :head="item.distributor ? `customer_details` : `company_details`"
                  class="pt-3"
                >
                  <!-- Company details -->
                  <template v-if="!companiesBeingEdited[item.id]">
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.name') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.name }}</span>
                    </div>
                    <div v-if="item.customer_account" class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.representative') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.representative }}</span>
                    </div>
                    <div v-if="item.customer_account" class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.customer_account') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.customer_account }}</span>
                    </div>
                    <div v-if="item.customer_account" class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.area_code') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.area }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.address') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.address }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.city') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.city }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.country') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ getCountryName(item.country) }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.zip') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{ item.zip }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.mailing_list') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{
                        (item.mailing_list && item.mailing_list.join(', ')) || '-'
                      }}</span>
                    </div>
                    <div class="py-4 sm:flex items-center border-b last:border-b-0">
                      <span class="sm:w-2/5 leading-5 text-secondary-dark"
                        >{{ $t('mygwf.components.Form.services') }}:</span
                      >
                      <span class="leading-5 sm:mt-0">{{
                        generateServicesText(item.services)
                      }}</span>
                    </div>
                  </template>

                  <!-- Action buttons -->
                  <template v-if="!companiesBeingEdited[item.id]" #footer>
                    <base-button
                      action="delete_company"
                      context="error"
                      :loading="companiesBeingDeleted[item.id]"
                      :disabled="companiesBeingDeleted[item.id]"
                      @click="onDeleteCompany(item)"
                    >
                      <base-icon icon="delete" />
                    </base-button>
                    <base-button @click="toggleCompanyEditState(item.id)" action="edit_company">
                      <base-icon icon="edit" />
                    </base-button>
                  </template>

                  <!-- Company edit -->
                  <company-form
                    v-else
                    :mode="'edit'"
                    :company-id="item.id"
                    @updated="toggleCompanyEditState(item.id)"
                    @close="toggleCompanyEditState(item.id)"
                  />
                </base-card>
              </div>
            </div>
          </div>
        </transition>
      </template>
    </Grid>

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <CompaniesFilters :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>
  </div>
</template>

<script>
import {
  BaseSelect,
  BaseIcon,
  BaseCard,
  BaseInput,
  BaseRadio,
  BaseButton,
  BaseFieldset,
  BaseButtonIcon,
  BaseModal,
} from '@/components/base'
import CompanyForm from './CompanyForm.vue'
import CompaniesFilters from './CompaniesFilters.vue'
import Grid from '@/components/shared/Grid.vue'
import {getCountryName} from '@/state/helpers/mixins.js'
import {mapGetters} from 'vuex'

export default {
  components: {
    BaseSelect,
    Grid,
    BaseIcon,
    BaseCard,
    BaseInput,
    BaseRadio,
    BaseButton,
    BaseFieldset,
    BaseButtonIcon,
    CompanyForm,
    CompaniesFilters,
    BaseModal,
  },

  data() {
    return {
      showFilterModal: false,
      visibleCompanies: [],
      companiesBeingEdited: {},
      companiesBeingDeleted: {},
      refresh: false,
      headers: {
        name: {
          label: 'company',
          order: false,
          className: 'w-1/3',
        },
        is_active: {
          label: 'status',
          order: false,
        },
        // Conditional object merging
        ...(this.$store.getters['auth/isStaff'] && {
          customer_account: {
            label: 'customer_account',
            order: false,
          },
          distributor: {
            label: 'distributed_by',
            order: false,
          },
        }),
        representative: {
          label: 'representative',
          order: false,
        },
        actions: {
          label: '',
          order: false,
          className: 'w-20',
        },
      },
      perPage: this.$store.getters['app/gridLimit'],

      filteredList: [],

      loading: false,
      clickedCompanyId: undefined,
    }
  },

  mixins: [getCountryName],

  computed: {
    ...mapGetters('companies', {
      companies: 'list',
    }),
    companiesMap() {
      const result = {}
      this.companies.forEach(e => (result[e.id] = e.name))
      return result
    },
    count() {
      return this.filteredCompanies.length
    },
    next() {
      return this.currentPage < this.totalPages
        ? `?limit=${this.perPage}&offset=${this.currentPage * this.perPage}`
        : null
    },
    previous() {
      return this.currentPage > 1
        ? `?limit=${this.perPage}&offset=${(this.currentPage - 2) * this.perPage}`
        : null
    },
    results() {
      return (
        this.filteredCompanies.slice(
          (this.currentPage - 1) * this.perPage,
          this.currentPage * this.perPage
        ) || []
      )
    },
    filteredCompanies() {
      return this.filter()
    },
    totalPages() {
      return Math.ceil(this.filteredCompanies.length / this.perPage)
    },
    currentPage() {
      const offset = parseInt(this.$route.query.offset, 10)
      return offset && offset > 0 ? 1 + Math.ceil(offset / this.perPage) : 1
    },
    paginateData() {
      return {
        perPage: this.perPage,
        currentPage: this.currentPage,
        totalPages: Math.ceil(this.filteredCompanies.length / this.perPage),
        totalEntries: this.count,
      }
    },
  },

  methods: {
    activateCompany(item) {
      this.clickedCompanyId = item.id
      this.loading = true
      this.$router.push({name: 'CompanyActivate', params: {id: item.id}})
    },

    filter() {
      const companyName = this.$route.query.name || ''
      const matcher = new RegExp(companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')

      return this.companies
        .filter(company => {
          const companyType = this.$route.query.is_distributor || ''
          if (companyType === 'true') {
            return company.is_distributor
          }
          if (companyType === 'false') {
            return !company.is_distributor
          }
          return true
        })
        .filter(company => {
          const companyStatus = this.$route.query.is_active || ''
          if (companyStatus === 'true') {
            return company.is_active
          }
          if (companyStatus === 'false') {
            return !company.is_active
          }
          return true
        })
        .filter(company => {
          const companyDistributor = parseInt(this.$route.query.distributor, 10) || undefined
          if (companyDistributor === undefined) {
            return true
          }
          return (company.distributor && company.distributor === companyDistributor) || false
        })
        .filter(company => {
          const customerAccount = this.$route.query.customer_account || ''
          if (customerAccount !== '') {
            return `${company?.area || ''}-${company?.customer_account || ''}`.includes(
              customerAccount
            )
          }
          return true
        })
        .filter(company => {
          return matcher.test(company.name)
        })
    },

    async onDeleteCompany(company) {
      if (this.companiesBeingDeleted[company.id]) {
        return
      }

      const shouldDelete = confirm(
        this.$t(`mygwf.components.Confirmation.company_confirm_delete`, {name: company.name})
      )
      if (!shouldDelete) {
        return
      }

      try {
        this.$set(this.companiesBeingDeleted, company.id, true)
        await this.$store.dispatch('companies/delete', {id: company.id})
        this.$store.dispatch('app/notification', {type: 'success', group: 'app', text: `deleted`})
      } catch (e) {
        this.$store.dispatch('app/notification', {type: 'error', group: 'app', text: `deleted`})
      } finally {
        this.$set(this.companiesBeingDeleted, company.id, false)
      }
    },

    async getCompanies() {
      this.$store.dispatch('app/startApiCall')
      try {
        await this.$store.dispatch('companies/getAll')
      } catch (error) {}
      this.$store.dispatch('app/finishApiCall')
    },

    toggleCompanyDetails(id) {
      if (!this.visibleCompanies.includes(id)) {
        return this.visibleCompanies.push(id)
      }
      return this.visibleCompanies.splice(this.visibleCompanies.indexOf(id), 1)
    },

    generateServicesText(companyServices) {
      if (!companyServices || companyServices.length === 0) {
        return '-'
      }

      const services = this.$store.getters['auth/services']
      return companyServices
        .map(id => services.find(s => s.id === id))
        .map(s => s?.name || '-')
        .join(', ')
    },

    toggleCompanyEditState(id) {
      this.$set(this.companiesBeingEdited, id, !this.companiesBeingEdited[id])
    },

    onFirstPageRequested() {
      this.currentPage = 1
      this.filteredList = this.filteredCompanies.slice(0, this.perPage)
    },

    onLastPageRequested() {
      this.currentPage = Math.ceil(this.filteredCompanies.length / this.perPage)
      this.filteredList = this.filteredCompanies.slice(
        (this.currentPage - 1) * this.perPage,
        this.filteredCompanies.length
      )
    },

    onNextPageRequested() {
      const totalPages = Math.ceil(this.filteredCompanies.length / this.perPage)
      if (this.currentPage === totalPages) {
        return
      }

      const startIndex = this.currentPage * this.perPage
      this.currentPage++
      const endIndex = this.currentPage * this.perPage
      this.filteredList = this.filteredCompanies.slice(startIndex, endIndex)
    },

    onPreviousPageRequested() {
      if (this.currentPage === 1) {
        return
      }

      const startIndex = (this.currentPage - 2) * this.perPage
      this.currentPage--
      const endIndex = this.currentPage * this.perPage
      this.filteredList = this.filteredCompanies.slice(startIndex, endIndex)
    },
  },
}
</script>
<style scoped>
.width-limit {
  max-width: 10rem;
}
@media (min-width: 1600px) {
  .width-limit {
    max-width: 18rem;
  }
}
</style>
